import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotifications,
} from "../../api/queries/notificationQueries";
import usePermissions from "../../hooks/usePermissions";
import { resolveNotificationLink } from "../../utils/notificationLinks";
import { formatDateTime, fromNow } from "../../utils/dateTime";
import { iconFor } from "../notifications/notificationFormat";
import NotificationsModal from "../notifications/NotificationsModal";
import "./NotificationBell.css";

const EMPTY = [];
const LIST_PARAMS = { page: 1, limit: 10 };

/** Top-bar bell (both views): unread badge, newest 10, mark one / all as read, View all (large view). */
const NotificationBell = () => {
  const navigate = useNavigate();
  const { hasHrAccess, employeeId } = usePermissions();
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const wrapRef = useRef(null);
  const unread = useUnreadNotifications().data?.unread ?? 0;
  const list = useNotifications(LIST_PARAMS, { enabled: open });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const items = list.data?.data || EMPTY;

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) =>
      wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openItem = (n) => {
    if (!n.readAt) markRead.mutate(n.id);
    const to = resolveNotificationLink(n.link, { hasHrAccess, employeeId });
    setOpen(false);
    if (to) navigate(to);
  };

  return (
    <div className="notif-wrap me-2" ref={wrapRef}>
      <button
        type="button"
        className="btn btn-sm notif-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unread ? `Notifications, ${unread} unread` : "Notifications"
        }
        aria-expanded={open}
        aria-haspopup="true"
      >
        <i className={`bi ${unread ? "bi-bell-fill" : "bi-bell"}`}></i>
        {unread > 0 && (
          <span className="notif-badge" aria-hidden="true">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-head">
            <strong>Notifications</strong>
            <div className="d-flex align-items-center gap-2">
              {unread > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                >
                  Mark all as read
                </button>
              )}
              <button
                type="button"
                className="btn-close notif-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              ></button>
            </div>
          </div>
          <div className="notif-list">
            {list.isLoading && (
              <div className="text-center py-4">
                <span
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            {list.isError && (
              <div className="notif-empty text-danger">
                Could not load notifications.
              </div>
            )}
            {!list.isLoading && !list.isError && items.length === 0 && (
              <div className="notif-empty">
                <i className="bi bi-bell-slash d-block fs-150 mb-1"></i>You're
                all caught up.
              </div>
            )}
            {items.map((n) => {
              const [icon, tone] = iconFor(n);
              return (
                <button
                  key={n.id}
                  type="button"
                  className={`notif-item ${n.readAt ? "" : "is-unread"}`}
                  onClick={() => openItem(n)}
                >
                  <span
                    className={`notif-icon notif-icon--${tone}`}
                    aria-hidden="true"
                  >
                    <i className={`bi ${icon}`}></i>
                  </span>
                  <span className="notif-body">
                    <span className="notif-title">{n.title}</span>
                    {n.message && (
                      <span className="notif-message">{n.message}</span>
                    )}
                    <span
                      className="notif-time"
                      title={formatDateTime(n.createdAt)}
                    >
                      {fromNow(n.createdAt)}
                    </span>
                  </span>
                  {!n.readAt && (
                    <span className="notif-dot" aria-label="Unread"></span>
                  )}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="notif-all"
            onClick={() => {
              setOpen(false);
              setShowAll(true);
            }}
          >
            View all notifications <i className="bi bi-arrow-right"></i>
          </button>
        </div>
      )}
      {showAll && <NotificationsModal onClose={() => setShowAll(false)} />}
    </div>
  );
};

export default NotificationBell;
